import { useState } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Loader2, LayoutDashboard, Users, KanbanSquare, Building2, CheckSquare, UserCog, Settings, LogOut, ShieldCheck, Menu } from "lucide-react";

const nav = [
  { to: "/espace-courtier/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/espace-courtier/prospects", label: "Prospects", icon: Users },
  { to: "/espace-courtier/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/espace-courtier/taches", label: "Tâches", icon: CheckSquare },
  { to: "/espace-courtier/assureurs", label: "Référentiel assureurs", icon: Building2 },
  { to: "/espace-courtier/utilisateurs", label: "Utilisateurs", icon: UserCog, roles: ["administrator", "sales_manager"] },
  { to: "/espace-courtier/parametres", label: "Paramètres", icon: Settings, roles: ["administrator", "sales_manager"] },
];

function NavItems({ items, onNavigate }) {
  return (
    <>
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.split("/").pop()}`} onClick={onNavigate}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "hover:bg-white/5 text-slate-300"}`}>
          <n.icon size={18} /> {n.label}
        </NavLink>
      ))}
    </>
  );
}

export function CrmLayout({ children }) {
  const { user, checked, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!checked || user === null) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;
  if (user === false) return <Navigate to="/espace-courtier" replace />;

  const items = nav.filter((n) => !n.roles || n.roles.includes(user.role));
  const doLogout = async () => { await logout(); navigate("/espace-courtier"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 shrink-0 brand-navy text-slate-300 flex-col hidden md:flex fixed h-screen">
        <div className="p-5 border-b border-white/10">
          {config.logo_url ? (
            <img src={config.logo_url} alt={config.brand_name} className="h-11 w-auto bg-white rounded-lg p-1.5" />
          ) : (
            <span className="flex items-center gap-2 font-display font-bold text-white"><ShieldCheck size={20} className="text-blue-400" />{config.brand_name}</span>
          )}
          <p className="text-xs text-slate-500 mt-2">Espace courtier · CRM</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <NavItems items={items} />
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-slate-400">{user.name}<br /><span className="text-slate-500">{user.email}</span></div>
          <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5" onClick={doLogout} data-testid="logout-button">
            <LogOut size={18} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>
      <div className="flex-1 md:ml-64 min-w-0">
        <div className="md:hidden brand-navy text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="text-white hover:bg-white/10" data-testid="crm-mobile-menu-button" aria-label="Ouvrir le menu">
                  <Menu size={22} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 brand-navy text-slate-300 border-white/10 p-0 flex flex-col">
                <div className="p-5 border-b border-white/10">
                  {config.logo_url ? (
                    <img src={config.logo_url} alt={config.brand_name} className="h-11 w-auto bg-white rounded-lg p-1.5" />
                  ) : (
                    <span className="flex items-center gap-2 font-display font-bold text-white"><ShieldCheck size={20} className="text-blue-400" />{config.brand_name}</span>
                  )}
                  <p className="text-xs text-slate-500 mt-2">Espace courtier · CRM</p>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                  <NavItems items={items} onNavigate={() => setMobileOpen(false)} />
                </nav>
                <div className="p-3 border-t border-white/10">
                  <div className="px-3 py-2 text-xs text-slate-400">{user.name}<br /><span className="text-slate-500">{user.email}</span></div>
                  <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5" onClick={doLogout} data-testid="logout-button-mobile">
                    <LogOut size={18} className="mr-2" /> Déconnexion
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            {config.logo_url ? (
              <img src={config.logo_url} alt={config.brand_name} className="h-9 w-auto bg-white rounded-md p-1" />
            ) : (
              <span className="font-display font-bold">{config.brand_name}</span>
            )}
          </div>
          <Button size="sm" variant="ghost" className="text-white" onClick={doLogout}><LogOut size={16} /></Button>
        </div>
        <div className="p-5 md:p-8 max-w-7xl mx-auto">{children}</div>
      </div>
    </div>
  );
}
